// i18n — TR source + EN overlay (call-agent-admin pattern).
// ─────────────────────────────────────────────────────────────────────────────
// The panel's source language is TURKISH: every JSX string is written in Turkish
// so the design file compares 1:1 and no t() wrapper is needed. When EN is picked,
// the DOM's text nodes (plus input placeholders and title attributes) are translated
// AFTER render, from the dictionary below.
//
// Two rules keep it safe:
//   1. Root <div key={'lang-'+lang}> — changing language remounts the tree, so the DOM
//      returns to fresh Turkish, then (if EN) is re-translated. State lives ABOVE that div.
//   2. useLayoutEffect (no dep array) runs after every commit, before paint, so even if
//      React rewrites a node the translation comes back and Turkish never "flashes".
// A MutationObserver additionally catches async content (fetch results, modals) that lands
// without an App-level re-render.

import { useEffect, useLayoutEffect } from 'react';
import type { Lang } from './types';

// Filled from the extraction pass. Key = EXACT rendered Turkish text; value = English.
// NOT in here on purpose: the call_lists / call_types LABELS (YATIRIM, TEBRİK, SLACK,
// ÇEKİM SORUNU…). They are the call-agent spreadsheet's own all-caps vocabulary and are
// printed verbatim in both languages, exactly as call-agent-admin's weekly report treats
// its sheet values. Translating them would make the panel and the sheet disagree about
// what a row is called.
const EN: Record<string, string> = {
  '· kalite ekibi performansı': '· quality team performance',
  '· kalite performansın ve QMS ekibinden gelen geri bildirimler': '· your quality performance and feedback from the QMS team',
  '(agent görür)': '(visible to agent)',
  '(İstanbul)': '(Istanbul)',
  '(mp3, wav, m4a, ogg · en fazla 60 MB)': '(mp3, wav, m4a, ogg · max 60 MB)',
  '← Önceki': '← Previous',
  '+ Yeni Değerlendirme': '+ New Evaluation',
  '0 puan': '0 points',
  'Ad Soyad': 'Full name',
  'Ad Soyad *': 'Full name *',
  'ad.soyad': 'first.last',
  'Agent — sadece kendi skorlarını görür': 'Agent — sees only their own scores',
  'Agent bulunamadı.': 'Agent not found.',
  'Agent itirazlarını okuyun, gerekirse puanı güncelleyin.': 'Read agent objections and update the score if needed.',
  'Agent panosuna ve raporlara işlendi.': 'Recorded to the agent dashboard and reports.',
  'Agent Portalı': 'Agent Portal',
  'Agent seçilmedi.': 'No agent selected.',
  'Agent sıralaması': 'Agent ranking',
  'Agent\'a iletilecek gözlemler…': 'Observations to share with the agent…',
  'Agent\'a iletilecek yanıt…': 'Reply to be sent to the agent…',
  Aktif: 'Active',
  Aktifleştir: 'Activate',
  'alır.': '.',
  'Arama Kalitesi:': 'Call Quality:',
  'Arama tipi': 'Call type',
  'Arama tipi *': 'Call type *',
  'Arama tipi seçilmedi.': 'No call type selected.',
  'Arama tipi seçilmeli.': 'A call type must be selected.',
  'Aranan üyenin BackOffice kullanıcı numarası.': "The called member's BackOffice user number.",
  Aylık: 'Monthly',
  Bekleyen: 'Open',
  'Bu değerlendirmede ses kaydı yok.': 'This evaluation has no recording.',
  'Bekleyen itiraz': 'Pending objections',
  'Bekleyen itiraz yok.': 'No open objections.',
  Bekliyor: 'Pending',
  'biri işaretlenirse çağrı, diğer puanlardan bağımsız olarak': 'below is checked, the call, independent of the other scores, receives a',
  'Bilinmeyen arama tipi.': 'Unknown call type.',
  'Bilinmeyen liste.': 'Unknown list.',
  'Bu çağrı için kayıt yüklenmedi.': 'No recording was uploaded for this call.',
  'Bu değerlendirmeye itiraz edilemez.': 'This evaluation cannot be objected to.',
  'Bu değerlendirmeye zaten itiraz edilmiş.': 'This evaluation has already been objected to.',
  'Bu dönem değerlendirilen': 'Evaluated this period',
  'Çağrı kaydı zorunlu.': 'A call recording is required.',
  'Bu puanda QMS notu zorunlu.': 'A QMS note is required at this score.',
  'Bu dönem değerlendirme': 'Evaluations this period',
  'bu dönem en az bir çağrısı puanlanan': 'had at least one call scored this period',
  'Bu dönemde değerlendirme yok.': 'No evaluations this period.',
  'Bu dönemde kritik hata yok.': 'No critical errors in this period.',
  'Bu dönemde puanlanmış kriter yok.': 'No scored criteria this period.',
  'Bu filtrelerle değerlendirme bulunamadı.': 'No evaluations found with these filters.',
  'Bu işlem için yetkiniz yok.': 'You are not authorized to perform this action.',
  'Bu itiraz zaten sonuçlandırılmış.': 'This objection has already been resolved.',
  'Bu kullanıcı adı zaten kullanılıyor.': 'This username is already in use.',
  'Çağrı Değerlendirmesi': 'Call Evaluation',
  'çağrı kaydı': 'call recording',
  'Çağrı kaydı': 'Call recording',
  'Çağrı kaydı *': 'Call recording *',
  'Çağrı kaydı her çağrıda zorunlu.': 'A call recording is required on every call.',
  'Çağrı kaydı yüklenmeden değerlendirme kaydedilemez.': 'The evaluation cannot be saved until a call recording is uploaded.',
  'Çağrı kaydını yükleyin, beş kriteri puanlayın ve varsa kritik hataları işaretleyin.': 'Upload the call recording, score the five criteria, and flag any critical errors.',
  'Çağrı kayıtlarını değerlendirin, kritik hataları izleyin ve agent performansını tek bir panoda takip edin.': 'Evaluate call recordings, track critical errors, and monitor agent performance from a single dashboard.',
  'Çağrı tarihi ve saati *': 'Call date and time *',
  'Çağrı tarihi zorunlu.': 'Call date is required.',
  'Çağrı tarihi geçersiz.': 'The call date is invalid.',
  'Çağrı tarihi gelecekte olamaz.': 'The call date cannot be in the future.',
  'Çağrı tarihi:': 'Call date:',
  'Değiştirirseniz çağrı, dönem raporlarında yeni tarihine taşınır.': 'If you change it, the call moves to its new date in the period reports.',
  'Çağrının gerçekleştiği an — bugünün değil; dönem raporu buna göre belirlenir.': 'When the call actually happened — not today; the report period is set from this.',
  Çıkış: 'Log out',
  'Çok fazla deneme yapıldı, bir dakika sonra tekrar deneyin.': 'Too many attempts, please try again in a minute.',
  'Çok İyi': 'Very Good',
  Değerlendiren: 'Evaluator',
  'Değerlendirilen agent': 'Agents evaluated',
  Değerlendirme: 'Evaluations',
  'Değerlendirme bulunamadı.': 'Evaluation not found.',
  'Değerlendirme kaydedildi': 'Evaluation saved',
  'Değerlendirmeyi düzenle': 'Edit evaluation',
  'Değerlendirme güncellenemedi.': 'The evaluation could not be updated.',
  'Değişikliği kaydet': 'Save change',
  'Değişiklik gerekçesi': 'Reason for the change',
  'Değişiklik gerekçesi *': 'Reason for the change *',
  'Değişiklik gerekçesi zorunlu.': 'A reason for the change is required.',
  'değişiklik gerekçesi zorunlu': 'A reason for the change is required',
  'bu değerlendirmeyi düzenleme yetkiniz yok': 'You are not allowed to edit this evaluation',
  'Puan geçmişinde görünür…': 'Shown in the score history…',
  'Not ekle': 'Add a note',
  'Kendi adınızla eklenecek not…': 'A note added under your own name…',
  'QMS notunun altında, adınız ve saatiyle agent\'a gösterilir.': 'Shown to the agent under the QMS note, with your name and time.',
  'Eklenen notlar': 'Added notes',
  'Düzenleme ve itiraz yanıtı sırasında bırakılan notlar, yazarının adıyla.': 'Notes left while editing or answering an objection, under their author\'s name.',
  'not metin olmalı': 'The note must be text',
  'Çağrı kaydı (değiştir)': 'Call recording (replace)',
  'Çağrı kayıtları': 'Call recordings',
  'Çağrı kayıtları *': 'Call recordings *',
  'Kaydı çıkar': 'Remove recording',
  'Kaydı sil': 'Delete recording',
  'Silmekten vazgeç': 'Undo removal',
  'Bu değerlendirmeye daha fazla çağrı kaydı eklenemez.': 'No more call recordings can be added to this evaluation.',
  'Silinmek istenen çağrı kaydı bulunamadı.': 'The call recording to be deleted was not found.',
  'Silinecek çağrı kayıtları okunamadı.': 'The recordings to be deleted could not be read.',
  'silinecek kayıtlar okunamadı': 'the recordings to be deleted could not be read',
  'silinecek kayıtlar bir dizi olmalı': 'the recordings to be deleted must be a list',
  'Önceki puan kalıcı olarak saklanır ve puan geçmişinde görünür.':
    'The previous score is stored permanently and appears in the score history.',
  '10 puan altındaki çağrılarda QMS notu zorunludur.':
    'A QMS note is required for any call that did not score 10.',
  'Açık itirazı olan değerlendirme düzenlenemez — önce itirazı yanıtlayın.':
    'An evaluation with an open objection cannot be edited — answer the objection first.',
  'Değerlendirme kaydedilemedi.': 'The evaluation could not be saved.',
  Değerlendirmeler: 'Evaluations',
  Değerlendirmelerim: 'My Evaluations',
  'Değerlendirmeyi aç': 'Open evaluation',
  'Değerlendirmeyi Kaydet': 'Save Evaluation',
  Detay: 'Details',
  'devam ediyor': 'in progress',
  'Devam etmek için giriş yapın.': 'Sign in to continue.',
  'Dosya çok büyük.': 'File is too large.',
  Durum: 'Status',
  'Değerlendirmelerine yapılan itirazlar ve sonuçları. Yanıtlama yetkisi süpervizördedir.': 'Objections filed against your evaluations and their outcomes. The supervisor answers them.',
  'Ekibinizden gelen itirazlar ve sonuçları.': 'Objections from your team and their outcomes.',
  'Ekip sıralaması': 'Team ranking',
  'eklendi.': 'added.',
  'Ekleniyor…': 'Adding…',
  'en az 8 karakter': 'at least 8 characters',
  Gelişimim: 'My Progress',
  Geliştirilmeli: 'Needs Improvement',
  'Genel Bakış': 'Overview',
  'Genel kalite ortalaması': 'Overall quality average',
  'Giriş yap': 'Sign in',
  'Giriş yapılamadı.': 'Could not sign in.',
  'Giriş yapılıyor…': 'Signing in…',
  'Gönderiliyor…': 'Sending…',
  'Güncellenemedi.': 'Could not update.',
  Günlük: 'Daily',
  Haftalık: 'Weekly',
  'Hangi kritere neden itiraz ettiğini yaz…': 'Write which criterion you object to and why…',
  'her biri o çağrıyı 0 yaptı': 'each one scored that call 0',
  'Her çağrı, aynı kalite standardıyla.': 'Every call, held to the same quality standard.',
  'Her güncelleme öncesi puan kalıcı olarak saklanır.': 'The score before each update is stored permanently.',
  'Her kriter 0–10 arası puanlanır ve ağırlığına göre toplam puana katılır. Aşağıdaki': 'Each criterion is scored from 0–10 and contributes to the total according to its weight. If one of the',
  'Her kriter ağırlığına göre toplam puana katılır.': 'Each criterion contributes to the total score by its weight.',
  'Hesap yok.': 'No accounts.',
  Hesaplar: 'Accounts',
  İncele: 'Review',
  'İncele ve yanıtla': 'Review and respond',
  'İşaretlenen her madde çağrıyı doğrudan': 'Each checked item makes the call directly',
  İtiraz: 'Objection',
  'İtiraz edildi': 'Objected',
  'İtiraz et': 'Object',
  'İtiraz gönderilemedi.': 'Objection could not be sent.',
  'İtiraz metni boş olamaz.': 'The objection text cannot be empty.',
  'İtiraz reddedildi': 'Objection rejected',
  'İtiraz sonuçlandırılamadı.': 'Objection could not be resolved.',
  'İtiraz yanıtı': 'Objection reply',
  'İtirazı reddet (puan aynı kalsın)': 'Reject objection (keep score unchanged)',
  İtirazın: 'Your objection',
  'İtirazın QMS uzmanına iletilir. Her değerlendirmeye bir kez itiraz edilebilir.': 'Your objection is forwarded to the QMS specialist. Each evaluation can be objected to once.',
  İtirazlar: 'Objections',
  İtirazlarım: 'My objections',
  'Yaptığın itirazlar, verilen yanıtlar ve sonuçları.': 'Your objections, the replies given and their outcomes.',
  'Yanıt bekleyen': 'Awaiting reply',
  'Yanıt bekleyen itirazın yok.': 'None of your objections are awaiting a reply.',
  'Henüz itiraz etmedin.': 'You have not objected yet.',
  'Puan korundu': 'Score kept',
  İyi: 'Good',
  Kalite: 'Quality',
  'Kalite Yönetimi': 'Quality Management',
  'karşılaştırılacak dönem yok': 'no period to compare',
  'Kaydediliyor…': 'Saving…',
  'Kaydetmek için beş kriterin tamamı puanlanmalı.': 'All five criteria must be scored to save.',
  'Kaydı görüntüle': 'View record',
  'Kayıt bulunamadı.': 'Record not found.',
  'Kayıt yok.': 'No records.',
  Kayıtlar: 'Records',
  'Kayıtlı aktif agent yok.': 'No active agents on record.',
  'Kendi hesabınızı pasife alamazsınız.': 'You cannot deactivate your own account.',
  'Kriter kırılımı': 'Criteria breakdown',
  'Kriter ortalaması': 'Criteria average',
  'Kriter puanları': 'Criterion scores',
  'Kritere göre ortalama': 'Average by criterion',
  Kritik: 'Critical',
  'Kritik hata': 'Critical errors',
  'Kritik Hata': 'Critical Error',
  'Kritik Hata (Fatal) Listesi': 'Critical Error (Fatal) List',
  'Kritik hata dağılımı': 'Critical error distribution',
  'Kritik hatalar': 'Critical errors',
  'kritik hatalardan': 'critical errors',
  'her çağrıda çağrı kaydı zorunlu': 'a call recording is required on every call',
  'Kullanıcı adı': 'Username',
  'Kullanıcı adı *': 'Username *',
  'Kullanıcı adı veya şifre hatalı.': 'Username or password is incorrect.',
  'Kullanıcı ekle': 'Add user',
  'Kullanıcı eklenemedi.': 'Could not add user.',
  'Kullanıcı ID': 'User ID',
  'Kullanıcı ID *': 'User ID *',
  'Kullanıcı ID geçersiz.': 'The user ID is invalid.',
  'Kullanıcı ID girilmeli.': 'A user ID must be entered.',
  'Kullanıcı ID zorunlu.': 'User ID is required.',
  Kayıt: 'Recording',
  Kullanıcılar: 'Users',
  'kuyruk boş': 'queue empty',
  Liste: 'List',
  'Liste *': 'List *',
  'Liste seçilmedi.': 'No list selected.',
  'Liste seçilmeli.': 'A list must be selected.',
  'Listeden gelir': 'Derived from the list',
  'Liste:': 'List:',
  Marka: 'Brand',
  'Marka:': 'Brand:',
  'Odak alanı:': 'Focus area:',
  Ortalama: 'Average',
  'Ortalama kalite skoru': 'Average quality score',
  'Oturum sona erdi, tekrar giriş yapın.': 'Your session has ended, please sign in again.',
  'Panel hesapları, rolleri ve marka yetkileri.': 'Panel accounts, roles, and brand permissions.',
  Panom: 'My Dashboard',
  Pasif: 'Inactive',
  'Pasife al': 'Deactivate',
  'Puan geçmişi': 'Score history',
  'Her çağrıda çağrı kaydı zorunlu.': 'A call recording is required on every call.',
  'Puan güncellemesi için yeni puanlar gerekli.': 'New scores are required to update the score.',
  'Puan güncellendi': 'Score updated',
  'Puanı güncelle': 'Update score',
  Puanlanmadı: 'Not Scored',
  'QMS notu & geri bildirim': 'QMS note & feedback',
  'QMS Puanlama Modeli': 'QMS Scoring Model',
  'QMS Uzmanı — yalnız değerlendirir; marka seçilirse o markaya kilitlenir':
    'QMS Specialist — evaluates only; locked to the brand if one is selected',
  Rapor: 'Report',
  Raporlar: 'Reports',
  Reddedildi: 'Rejected',
  Rol: 'Role',
  'Rol *': 'Role *',
  'Rol ve yetkileriniz yöneticiniz tarafından atanır.': 'Your role and permissions are assigned by your administrator.',
  'Sadece kritik hatalı': 'Critical errors only',
  'SLACK / FORM / TAKİP için arama sebebi ayrıca seçilir.':
    'For SLACK / FORM / TAKİP the call reason is selected separately.',
  'Sayfa bulunamadı.': 'Page not found.',
  'Seçin…': 'Select…',
  'Skor kriter ağırlıklarına göre otomatik hesaplanır ve sunucuda yeniden doğrulanır. Kaydedince agent panosuna ve dönem raporuna işlenir.': 'The score is calculated automatically from the criteria weights and re-verified on the server. Once saved, it is recorded to the agent dashboard and the period report.',
  'Sonraki →': 'Next →',
  Sonuçlanan: 'Resolved',
  'Sunucu hatası. Tekrar deneyin.': 'Server error. Please try again.',
  'Süpervizör — değerlendirir, itiraz yanıtlar, kullanıcı yönetir; marka seçilirse o markaya kilitlenir':
    'Supervisor — evaluates, answers objections, manages users; picking a brand locks them to it',
  Şifre: 'Password',
  'Şifre *': 'Password *',
  'Şifre çok kısa.': 'Password is too short.',
  'Şifre güncellenemedi.': 'Could not update password.',
  'şifresi güncellendi — açık oturumları kapatıldı.': 'password updated — active sessions closed.',
  // self-service password change (pages/ChangePassword.tsx)
  'Şifre değiştir': 'Change password',
  'Değişiklik sonrası diğer oturumlarınız kapatılır.': 'Your other sessions are closed after the change.',
  'Mevcut şifre': 'Current password',
  'Yeni şifre': 'New password',
  'Yeni şifre (tekrar)': 'New password (again)',
  'Şifreler eşleşmiyor.': 'The passwords do not match.',
  'Mevcut şifre hatalı.': 'Your current password is incorrect.',
  'Şifreyi güncelle': 'Update password',
  'Şifreniz güncellendi.': 'Your password has been updated.',
  Kapat: 'Close',
  'takip gerekiyor': 'follow-up needed',
  tamamlandı: 'completed',
  'Tarayıcınız ses oynatmayı desteklemiyor.': 'Your browser does not support audio playback.',
  Tarih: 'Date',
  'Tarih:': 'Date:',
  'Tekrar hoş geldiniz': 'Welcome back',
  'Tema değiştir': 'Toggle theme',
  'temiz dönem': 'clean period',
  'temiz dönem · tebrikler': 'clean period · congratulations',
  'Tüm değerlendirmeler →': 'All evaluations →',
  'Tüm dönemler': 'All periods',
  'Tüm durumlar': 'All statuses',
  'Tüm değerlendiriciler': 'All evaluators',
  'Tüm temsilciler': 'All agents',
  'Çağrı tarihi': 'Call date',
  'Değerlendirme tarihi': 'Evaluation date',
  'Değerlendirme saati': 'Evaluated at',
  'Dönem penceresi hangi tarihe uygulansın': 'Which date the period window applies to',
  'Değerlendirici geçersiz.': 'Invalid evaluator.',
  'Tarih ekseni geçersiz.': 'Invalid date axis.',
  Tümü: 'All',
  Vazgeç: 'Cancel',
  'Değerlendirmeyi sil': 'Delete evaluation',
  'Değerlendirme silinemedi.': 'The evaluation could not be deleted.',
  'Değerlendirme, puanları ve çağrı kayıtları kalıcı olarak silinir. Geri alınamaz.': 'The evaluation, its scores and its call recordings are permanently deleted. This cannot be undone.',
  'Evet, kalıcı olarak sil': 'Yes, delete permanently',
  'Siliniyor…': 'Deleting…',
  'Temsilci Raporu': 'Agent Report',
  Temsilci: 'Agent',
  'Temsilci seçin…': 'Select an agent…',
  'Raporu görüntülemek için bir temsilci seçin.': 'Select an agent to view their report.',
  'Bu markada aktif temsilci yok.': 'No active agents in this brand.',
  'Temsilci bazında kalite raporu': 'Per-agent quality report',
  'Tam sayfa aç': 'Open full page',
  'Bu raporu tek başına aç': 'Open this report on its own',
  'yanıt bekliyor': 'awaiting response',
  'yapar.': '.',
  Yayında: 'Published',
  'Yeni Değerlendirme': 'New Evaluation',
  'Yeni değerlendirme gir': 'Enter a new evaluation',
  'Yeni kullanıcı': 'New user',
  'Yeni puanı kaydet': 'Save new score',
  'Yeni puanlama': 'New scoring',
  'Yeni toplam:': 'New total:',
  'Yönetici — her şey': 'Admin — everything',
  'Yükleniyor…': 'Loading…',
  Merhaba: 'Hello',
  'önceki dönem:': 'previous period:',
  'tarafından güncellendi': 'updated',
  // ── 2026-09-03 redesign (Claude Design mockup) ──────────────────────────────
  'Sistem aktif': 'System active',
  SÜPERVİZÖR: 'SUPERVISOR',
  YÖNETİCİ: 'ADMIN',
  'Çağrı kalite yönetimi paneli': 'Call quality management panel',
  Giriş: 'Sign in',
  'Devam etmek için hesabınıza giriş yapın.': 'Sign in to your account to continue.',
  'Ağırlıklı skor': 'Weighted score',
  'Kritik hata · çağrı otomatik 0,0 aldı': 'Critical error · the call automatically scored 0.0',
  'QMS puanlama modeli · 5 kriter, toplam 100 ağırlık puanı.': 'QMS scoring model · 5 criteria, 100 weight points in total.',
  'Her kriter 0–10 arası puanlanır ve ağırlığına göre toplam puana katılır. Kritik hata listesinden bir madde işaretlenirse çağrı, diğer puanlardan bağımsız olarak 0 alır.': 'Each criterion is scored 0–10 and contributes to the total by its weight. If any item on the critical error list is checked, the call scores 0 regardless of the other scores.',
  'Her satır 0–10 · ağırlık toplam puana yansır': 'Each row 0–10 · the weight feeds the total',
  'İşaretlenen her madde çağrıyı doğrudan 0 puan yapar.': 'Each checked item makes the call score 0 directly.',
  'Açık itiraz': 'Open objections',
  'Değ.': 'Rev.',
  Panosu: 'View',
  'bu dönemde kritik hata yok': 'no critical errors this period',
  'Temiz dönem.': 'Clean period.',
  'kendi çağrıların': 'your own calls',
  'seçili dönemdeki tüm çağrılar': 'all calls in the selected period',
  'seçili dönemde değerlendirilen çağrılar': 'calls evaluated in the selected period',
  'tüm çağrılar': 'all calls',
  Saat: 'Time',
  Çağrı: 'Call',
  Önceki: 'Previous',
  Sonraki: 'Next',
  'İtiraz listesi': 'Objection list',
  'QMS notu': 'QMS note',
  'QMS notu *': 'QMS note *',
  'QMS notu ve agent\'a geri bildirim': 'QMS note & feedback to the agent',
  'QMS notu ve agent\'a geri bildirim *': 'QMS note & feedback to the agent *',
  'Yanıt bekleniyor': 'Awaiting reply',
  'Yeni toplam': 'New total',
  'Mevcut puanlar forma yüklendi; değiştirip sağdan kaydedin.': 'The current scores are loaded into the form; edit them and save on the right.',
  'Bu değerlendirme için itiraz süreci': 'The objection process for this evaluation is',
  'Çağrı bilgileri': 'Call details',
  'Agent, kayıt ve çağrı künyesi': 'Agent, recording and call header',
  'mp3, wav, m4a, ogg · en fazla 60 MB': 'mp3, wav, m4a, ogg · max 60 MB',
  'Agent panosunda görünür': 'Visible on the agent dashboard',
  'Bu kayıt görüntülenemiyor.': 'This record cannot be shown.',
  'Değerlendirmelere dön': 'Back to evaluations',
  'Çağrı kaydı boş, dosyayı tekrar yükleyin.': 'The call recording is empty — upload the file again.',
  'Bilinmeyen marka.': 'Unknown brand.',
  'Hesap durumu geçersiz.': 'Invalid account status.',
  'Yanıt metni geçersiz.': 'Invalid reply text.',
  'Puanlar okunamadı.': 'The scores could not be read.',
  'Kritik hata listesi okunamadı.': 'The critical-mistake list could not be read.',
  'Değerlendirmeyi kaydet': 'Save evaluation',
  'Tüm cevapları temizle': 'Clear all answers',
  'EN İYİ': 'BEST',
  'EN ZAYIF': 'WEAKEST',
  'En iyi ve en zayıf': 'Best and weakest',
  'Agent raporu': 'Agent report',
  'Kriter kırılımım': 'My criteria breakdown',
  'Odak:': 'Focus:',
  'Rol ve marka birlikte atanır': 'Role and brand are assigned together',
  'Pasif hesap giriş yapamaz; açık oturumları kapatılır': 'An inactive account cannot sign in; its open sessions are closed',
  'Şifre sıfırla': 'Reset password',
  '· haftalık dönem': '· weekly period',
  '· aylık dönem': '· monthly period',
  // ── agent home (pages/MyDashboard.tsx) ──────────────────────────────────────────────
  'Bu hafta henüz değerlendirme yok': 'No evaluations yet this week',
  'puan bekleniyor': 'awaiting a score',
  'Son değerlendirmem': 'My latest evaluation',
  'Henüz değerlendirilmiş bir çağrın yok.': 'None of your calls has been evaluated yet.',
  'Çağrı kayıtlarını QMS ekibi yükler; ilk değerlendirmen yapıldığında burada görünür.':
    'The QMS team uploads the call recordings; your first evaluation will appear here once it is done.',
  'Kritik hata işaretlendi · bu çağrı 0,0 aldı.': 'A critical error was flagged · this call scored 0.0.',
  'Detayı aç': 'Open details',
  'Önceki değerlendirmelerim': 'My earlier evaluations',
  'İtiraz durumum': 'My objection status',
  'İtiraz edebileceğin değerlendirme': 'Evaluations you can object to',
  'puanına katılmıyorsan her birine bir kez itiraz edebilirsin':
    'if you disagree with a score you may object once to each of them',
  'Yanıt bekleyen itirazın': 'Your objections awaiting a reply',
  'QMS uzmanı inceliyor': 'A QMS specialist is reviewing it',
  'İtirazın QMS ekibinde · yanıt bekleniyor.': 'Your objection is with the QMS team · awaiting a reply.',
  'İtirazın kabul edildi, puanın güncellendi.': 'Your objection was accepted and your score was updated.',
  'İtirazın incelendi, puan korundu.': 'Your objection was reviewed and the score was kept.',
  'Sistem nasıl çalışıyor?': 'How does it work?',
  'QMS uzmanı, çağrı kaydını sisteme yükleyerek ve kriterler üzerinden puan verir.':
    'A QMS specialist uploads the call recording and scores it against the criteria.',
  'Değerlendirmen yayınlandığında burada görünür; arama kaydını da dinleyerek verilen puanı değerlendirebilirsin.':
    'Your evaluation appears here once published; you can also listen to the call recording and review the score you were given.',
  'Katılmadığın bir skora bir kez itiraz edebilirsin; Supervisor kontrol ederek gerekirse tekrar puan verir.':
    'You may object once to a score you disagree with; a supervisor reviews it and re-scores if needed.',
  'Çağrında kritik hata olduğunda otomatik 0 puan alırsın. (Verilen diğer puanlar iyi olsa da)':
    'If your call has a critical error, you automatically get 0 points (even if your other scores are good).',
  'Çağrı kayıtlarını agent yüklemez — kayıtları sisteme QMS ekibi ekler.':
    'Agents do not upload call recordings — the QMS team adds them to the system.',
  // ── server-supplied strings (src/engine/access.js role labels, migrations/001 criteria +
  //    critical_rules rows, API error messages). The DB rows are seeded once and only change by
  //    migration; a changed row simply falls back to Turkish until this map is updated.
  'QMS Uzmanı': 'QMS Specialist',
  Süpervizör: 'Supervisor',
  Yönetici: 'Admin',
  'Açılış ve İsim Teyidi': 'Opening & Name Confirmation',
  'Arama Tipi İçerik Uyumu': 'Call-Type Content Fit',
  'İletişim Kalitesi': 'Communication Quality',
  'Ürün Bilgisi': 'Product Knowledge',
  'Kapanış ve Güvenli Site bilgisi': 'Closing & Safe-Site Information',
  'Listenin gerektirdiği özel soruların sorulması, doğru bilgi verilmesi': 'Asking the questions the list requires, giving correct information',
  'Diksiyon, empati, yasaklı kelime': 'Diction, empathy, prohibited words',
  'Profil analizi, doğru bonus/oyun önerisi, backoffice ve website kontrolü': 'Profile analysis, the right bonus/game suggestion, backoffice and website check',
  'İsim teyidinin alınmaması': 'Name confirmation not taken',
  'Backoffice hesabının açılmaması ve ezbere (ihtiyaç dışı/yanlış) bonus sunulması': 'Backoffice account not opened and a rote (unneeded/wrong) bonus offered',
  'Kayıp yaşayan üyeye "Şans faktörü, olabilir" gibi motivasyon kırıcı ifadeler kullanılması': 'Using discouraging phrases such as "It\'s down to luck, it happens" with a member who lost',
  'Müsait olmayan üyeye sadece "Peki" demek': 'Replying only "Okay" to a member who is not available',
  'Çağrı sonunda "yardımcı" anonsu yapılmaması': 'No "anything else I can help with" announcement at the end of the call',
  'Güncel adres teyit kanallarının ve 7/24 canlı destek bilgisinin hatırlatılmaması': 'Not reminding of the current-address verification channels and 24/7 live support',
  'Oyuncunun kontrol nedeniyle 20 saniyeden fazla sessiz ve onaysız hatta bekletilmesi': 'Keeping the player on a silent, unconsented hold for more than 20 seconds',
  'Müsait olmayan veya tekrar aranması gereken üye için yeni randevu oluşturulmaması': 'No new appointment created for a member who was unavailable or must be called back',
  'bu değerlendirmeye itiraz edilemez': 'This evaluation cannot be objected to.',
  'kullanıcı adı 3–40 karakter, harf/rakam/._- olmalı': 'Username must be 3–40 characters: letters, digits, . _ -',
  // Weekly reports (owner 2026-09-29, Task Board #145/#146).
  'Haftalık Raporlar': 'Weekly Reports',
  'Markandaki her temsilci için haftalık genel rapor. Son teslim: haftanın ardından gelen Salı 10:30.': 'A weekly overall report for every agent of your brand. Due: the Tuesday after the week, 10:30.',
  'QMS uzmanlarının temsilci bazında haftalık genel raporları.': 'The QMS specialists\' weekly overall reports, per agent.',
  'Bu haftanın bütün raporları gönderildi.': 'Every report for this week has been sent.',
  'Bu seçimde görebileceğiniz haftalık rapor yok — raporları yalnız markanın süpervizörü ve yönetici görür.': 'No weekly reports you can see for this selection — they are visible only to the brand\'s supervisor and admins.',
  'Geç gönderildi': 'Sent late',
  'Gönderildi': 'Sent',
  'Eksik': 'Missing',
  'Rapor yaz': 'Write report',
  'Düzenle': 'Edit',
  'Gönder': 'Send',
  'Rapor kaydedilemedi.': 'The report could not be saved.',
  'Temsilcinin bu haftaki genel performansı, güçlü yönleri ve gelişim alanları…': 'The agent\'s overall performance this week, strengths and areas to improve…',
  'Haftalık rapor hatırlatması': 'Weekly report reminder',
  'Haftalık raporların son teslimi geçti': 'Weekly reports are overdue',
  'Hafta': 'Week',
  'Hafta geçersiz.': 'Invalid week.',
  'Bu hafta henüz başlamadı.': 'This week has not started yet.',
  'Rapor metni boş olamaz.': 'The report text cannot be empty.',
  'Rapor çok uzun.': 'The report is too long.',
  'Bu temsilci için bu haftanın raporu başka bir uzman tarafından gönderilmiş.': 'Another specialist has already sent this week\'s report for this agent.',
  'Son teslim geçti, rapor artık düzenlenemez.': 'The deadline has passed; the report can no longer be edited.',
  // Assigned calls (owner 2026-10-02, Task Board #163).
  'Atanan Çağrılar': 'Assigned Calls',
  'Bot her sabah dün çalışan her temsilcinin günlük kotasını dünkü çağrılarından sana atar. Değerlendir ile form hazır açılır; kaydı ekleyip puanla.': 'Every morning the bot assigns you each call agent\'s daily quota from the calls they made yesterday. Evaluate opens the form pre-filled; add the recording and score it.',
  'Botun her sabah dün çalışan her temsilcinin günlük kotasını QMS uzmanlarına atadığı çağrılar.': 'The calls the bot assigns to the QMS specialists every morning: each call agent\'s daily quota from yesterday.',
  'Başkası açtı yetmezse meşgulden; bir tür eksik kalırsa önce olumludan tamamlanır.': 'Too few someone-else-answered calls are made up from busy; any type that runs short is filled from positive first.',
  'Başkası Açtı': 'Someone else answered',
  'Olumsuz': 'Negative',
  'Aranmak İstemiyor': 'Do not call',
  'Şimdi dağıt': 'Assign now',
  'Gün': 'Day',
  'Otomatik atama kapalı — entegrasyon anahtarı ayarlı değil.': 'Automatic assignment is off — the integration key is not set.',
  'Bugün için atanmış çağrı yok.': 'No calls assigned for today.',
  'Bu gün için atanmış çağrı yok.': 'No calls assigned for this day.',
  'Çağrılarım': 'My calls',
  'Sonuç': 'Outcome',
  'Olumlu': 'Positive',
  'Kapattı': 'Hung up',
  'Meşgul': 'Busy',
  'Değerlendirildi': 'Evaluated',
  'Atlandı': 'Skipped',
  'Değerlendir': 'Evaluate',
  'Atla': 'Skip',
  'Geri al': 'Undo',
  'Neden atlanıyor? (ör. kayıt bulunamadı, yanlış çağrı)': 'Why skip it? (e.g. recording not found, wrong call)',
  'Atama çalışmadı.': 'The assignment did not run.',
  'Bugün herkese zaten çağrı atanmış.': 'Everyone already has today\'s calls.',
  'İşlem başarısız.': 'The action failed.',
  'Atanan çağrılara dön': 'Back to assigned calls',
  'Bu atanan çağrı zaten kapandı — kaydedilen değerlendirme atamaya bağlanmaz.': 'This assigned call is already closed — the evaluation you save will not be linked to it.',
  'Atanan çağrı bulunamadı.': 'Assigned call not found.',
  'Bu atanan çağrı zaten kapandı.': 'This assigned call is already closed.',
  'Atanan çağrının agentı seçilen agentla aynı değil.': 'The assigned call belongs to a different agent.',
  'Atanan çağrı geçersiz.': 'Invalid assigned call.',
  'Atlama gerekçesi zorunlu.': 'A reason for skipping is required.',
  'Gerekçe çok uzun.': 'The reason is too long.',
  'Otomatik atama kapalı.': 'Automatic assignment is off.',
  'Gün geçersiz.': 'Invalid day.',
  'Kayıt hazır': 'Recording ready',
  // Precise call tracking (migration 019): the softphone's record of an assigned call, and why
  // the recording fetcher could not stage one (Assignments.tsx REC_MISS_LABEL).
  'Konuşma': 'Talk time',
  'Cevaplandı': 'Answered',
  'Cevapsız': 'Not answered',
  'tahmini': 'estimated',
  'eşleşen çağrı bulunamadı': 'no matching call found',
  'çağrının kaydı yok': 'the call has no recording',
  'çağrı cevaplanmamış': 'the call was not answered',
  'gelen çağrı': 'incoming call',
  'başka temsilcinin çağrısı': 'another agent\'s call',
  'çağrı sonuçtan sonra başlamış': 'the call started after the outcome',
  'çağrı hâlâ sürüyordu': 'the call was still running',
  'çağrı çok eski': 'the call is too old',
  'dahili uyuşmuyor': 'extension mismatch',
  // Haftanın Temsilcisi · 24 saat on Genel Bakış (Task Board #292/#293, AgentOfWeek.tsx) — the
  // same words call-agent-admin's card uses in EN.
  'Haftanın Temsilcisi · 24 saat': 'Agent of the Week · 24h',
  'Sıra': 'Rank',
  'QMS puanı': 'QMS points',
  'Başarı puanı': 'Success points',
  'Yatırım puanı': 'Deposit points',
  'Kritik ceza': 'Critical penalty',
  'Skor': 'Score',
  'Canlı': 'Live',
  'Bayat': 'Stale',
  'Bu hafta değerlendirme yok': 'No evaluations this week',
  'Bu hafta henüz sıralanacak temsilci yok.': 'No agents to rank yet this week.',
  'Son yenileme başarısız — gösterilen sıralama son başarılı okumadan.': 'The last refresh failed — showing the ranking from the last successful read.',
  'QMS puanları okunamadı — herkes 0 QMS puanıyla sıralandı; bu gerçek sıralama değil.': 'QMS scores could not be read — everyone is ranked with 0 QMS points; this is not the real ranking.',
  'Sıralama ve puanlar call-agent-admin Haftalık Rapor\'daki Haftanın Temsilcisi kartının aynısıdır; her dakika yenilenir. Değerlendirmesi olmayan temsilci 0 QMS puanı alır.':
    'Ranking and points are the same as the Agent of the Week card in call-agent-admin\'s Weekly Report; refreshed every minute. An agent with no evaluation gets 0 QMS points.',
  // The agent's copy on Gelişimim (Task Board #298/#299).
  'Ekibinin bu haftaki sıralaması; her dakika yenilenir. Değerlendirmesi olmayan temsilci 0 QMS puanı alır.':
    'Your team\'s ranking this week; refreshed every minute. An agent with no evaluation gets 0 QMS points.',
  'Sen': 'You',
  'Veri alınamadı.': 'Could not load data.',
  // Users page row editor (Task Board #294/#295).
  'Kaydet': 'Save',
  'Tüm markalar': 'All brands',
  'Kendi rolünüzü değiştiremezsiniz': 'You cannot change your own role',
  'Agent adı call-agent-admin\'deki adıyla eşleşir (büyük/küçük harf önemsiz). Harfini değiştirirseniz skor beslemesi ve çağrı ataması o temsilciyi bulamaz.':
    'An agent\'s name is matched to their name in call-agent-admin (case does not matter). If you change its letters, the score feed and call assignment can no longer find that agent.',
};

/** Turkish month names → English, applied as a last pass so dates inside any label translate. */
const MONTHS: [string, string][] = [
  ['Ocak', 'January'], ['Şubat', 'February'], ['Mart', 'March'], ['Nisan', 'April'],
  ['Mayıs', 'May'], ['Haziran', 'June'], ['Temmuz', 'July'], ['Ağustos', 'August'],
  ['Eylül', 'September'], ['Ekim', 'October'], ['Kasım', 'November'], ['Aralık', 'December'],
];
const MONTH_RE = MONTHS.map(([tr, en]) => [new RegExp(`(?<!\\p{L})${tr}(?!\\p{L})`, 'gu'), en] as const);
function swapMonths(s: string): string {
  let out = s;
  for (const [re, en] of MONTH_RE) out = out.replace(re, en);
  return out;
}

/**
 * Translate one whole string. Dictionary first, then number/value-bearing regex patterns.
 * Returns null when nothing matches (the node is left untouched).
 *
 * NOTE: translation is per TEXT NODE. Interpolated JSX like `{x} puan` renders "x" and
 * " puan" as SEPARATE nodes and never matches — such strings are handled by the regexes
 * below or left as-is.
 */
export function translatePhrase(x: string): string | null {
  const direct = translateExact(x);
  const out = swapMonths(direct ?? x);
  return out !== x ? out : null;
}

function translateExact(x: string): string | null {
  if (EN[x] != null) return EN[x];
  let m: RegExpMatchArray | null;
  if ((m = x.match(/^(\d+) kayıt$/))) return m[1] + ' records';
  if ((m = x.match(/^Bu puan için zorunlu: (\d+) puanın altındaki her çağrı açıklama ister\.$/))) {
    return `Required at this score: every call below ${m[1]} needs an explanation.`;
  }
  if ((m = x.match(/^Yalnızca (\d+) puanın altındaki çağrılarda zorunlu\.$/))) {
    return `Only required for calls scoring below ${m[1]}.`;
  }
  if ((m = x.match(/^(\d+) itiraz$/))) return m[1] + ' objections';
  // Weekly reports (owner 2026-09-29): counts and the deadline are data.
  if ((m = x.match(/^(\d+) değerlendirme$/))) return m[1] + ' evaluations';
  if ((m = x.match(/^(\d+) kritik$/))) return m[1] + ' critical';
  if ((m = x.match(/^(\d+)\/(\d+) gönderildi$/))) return m[1] + '/' + m[2] + ' sent';
  if ((m = x.match(/^Son teslim: (.+) · (\d+) rapor eksik$/))) return 'Due: ' + m[1] + ' · ' + m[2] + ' reports missing';
  if ((m = x.match(/^Son teslim geçti \((.+)\) · (\d+) rapor eksik$/))) return 'Deadline passed (' + m[1] + ') · ' + m[2] + ' reports missing';
  if ((m = x.match(/^· son teslim (.+)$/))) return '· due ' + m[1];
  if ((m = x.match(/^(\d+) temsilci$/))) return m[1] + ' agents';
  // Assigned calls (Task Board #163): counts, times and names are data.
  if ((m = x.match(/^(\d+)\/(\d+) değerlendirildi$/))) return m[1] + '/' + m[2] + ' evaluated';
  if ((m = x.match(/^(\d+) değerlendirildi · (\d+) bekliyor · (\d+) atlandı$/))) return m[1] + ' evaluated · ' + m[2] + ' open · ' + m[3] + ' skipped';
  if ((m = x.match(/^Her gün (\S+) sonrası, dünkü çağrılardan · dün çalışan her temsilciye günlük kota$/))) {
    return 'Daily after ' + m[1] + ', from yesterday\'s calls · a daily quota for every agent who worked';
  }
  if ((m = x.match(/^(.+): temsilci başına günde (\d+) — (\d+) olumsuz \(olumsuz \/ aranmak istemiyor \/ kapattı\) · (\d+) olumlu · (\d+) başkası açtı · (\d+) meşgul · haftada (\d+)$/))) {
    return m[1] + ': ' + m[2] + ' per agent a day — ' + m[3] + ' negative (negative / do not call / hung up) · ' + m[4] + ' positive · ' + m[5] + ' someone else answered · ' + m[6] + ' busy · ' + m[7] + ' a week';
  }
  if ((m = x.match(/^Bu hafta · (.+) · (.+): (\d+)(\/\d+)? atandı · (\d+) değerlendirildi$/))) {
    return 'This week · ' + m[1] + ' · ' + m[2] + ': ' + m[3] + (m[4] || '') + ' assigned · ' + m[5] + ' evaluated';
  }
  if ((m = x.match(/^Bugün · (.+)$/))) return 'Today · ' + m[1];
  if ((m = x.match(/^Atanan çağrı · (Olumsuz|Aranmak İstemiyor|Olumlu|Kapattı|Meşgul|Başkası Açtı) · (.+)$/))) return 'Assigned call · ' + (EN[m[1]] || m[1]) + ' · ' + m[2];
  if ((m = x.match(/^(.+): (\d+) çağrı atandı( \(eksik kaldı\))?( · eşleşmeyen isim: (\d+))?$/))) {
    return m[1] + ': ' + m[2] + ' calls assigned' + (m[3] ? ' (short)' : '') + (m[4] ? ' · unmatched names: ' + m[5] : '');
  }
  if ((m = x.match(/^(.+): kaynak okunamadı, sonra tekrar denenecek$/))) return m[1] + ': source unreachable, will retry later';
  if ((m = x.match(/^ Kayıt otomatik getirildi \((\d+)\) — dinleyip puanlayın\.$/))) return ' Recording fetched automatically (' + m[1] + ') — listen and score.';
  // Precise call tracking (migration 019): talk time, fetcher misses, the bot's last run.
  if ((m = x.match(/^( ?)Kayıt yok: (.+)$/))) return m[1] + 'No recording: ' + (EN[m[2]] || m[2]);
  if ((m = x.match(/^Konuşma süresi: (\S+)( \(tahmini\))?$/))) return 'Talk time: ' + m[1] + (m[2] ? ' (estimated)' : '');
  if ((m = x.match(/^Botun son çalışması: (.+)$/))) return 'Bot\'s last run: ' + m[1];
  if ((m = x.match(/^(.+) · (.+): kaynak okunamadı$/))) return m[1] + ' · ' + m[2] + ': source unreachable';
  if ((m = x.match(/^(.+) · (.+): (\d+) atandı( · (\d+) eksik)?$/))) return m[1] + ' · ' + m[2] + ': ' + m[3] + ' assigned' + (m[4] ? ' · ' + m[5] + ' short' : '');
  if ((m = x.match(/^Eşleşmeyen isim \((\d+)\): (.+)$/))) return 'Unmatched names (' + m[1] + '): ' + m[2];
  // The list's agent filter marks a deactivated account: "Name · brand (pasif)" / "Name (pasif)".
  if ((m = x.match(/^(.+) \(pasif\)$/))) return m[1] + ' (inactive)';
  if ((m = x.match(/^Temsilci bazında kalite raporu · (.+)$/))) return 'Per-agent quality report · ' + m[1];
  if ((m = x.match(/^(.+) · seçili dönemin kalite raporu$/))) return m[1] + ' · quality report for the selected period';
  if ((m = x.match(/^(\d+) hesap$/))) return m[1] + ' accounts';
  if ((m = x.match(/^(.+) · önceki döneme göre$/))) return m[1] + ' · vs previous period';
  if ((m = x.match(/^önceki dönem: (\d+)$/))) return 'previous period: ' + m[1];
  if ((m = x.match(/^(.+) ekibi içinde$/))) return 'within the ' + m[1] + ' team';
  if ((m = x.match(/^İstek başarısız \((\d+)\)\.$/))) return 'Request failed (' + m[1] + ').';
  if ((m = x.match(/^(.+) eklendi\.$/))) return m[1] + ' added.';
  if ((m = x.match(/^(.+) güncellendi\.$/))) return m[1] + ' updated.';
  // Haftanın Temsilcisi subtitle — the weights come from call-agent-admin's formula block.
  if ((m = x.match(/^QMS %(\d+) · Başarı oranı %(\d+) · Yatırım %(\d+) · Kritik hata −(\S+)$/))) {
    return `QMS ${m[1]}% · Success rate ${m[2]}% · Deposits ${m[3]}% · Critical mistake −${m[4].replace(',', '.')}`;
  }
  if ((m = x.match(/^Sıralama şu an okunamadı \((\S+)\) — her dakika yeniden deneniyor\.$/))) return 'The ranking cannot be read right now (' + m[1] + ') — retrying every minute.';
  if ((m = x.match(/^Sıralama yenilenemedi: (.+)$/))) return 'The ranking could not be refreshed: ' + (EN[m[1]] || m[1]);
  if ((m = x.match(/^(.+) şifresi güncellendi — açık oturumları kapatıldı\.$/))) return m[1] + '’s password updated — active sessions closed.';
  if ((m = x.match(/^(.+) · Kalite Raporu$/))) return m[1] + ' · Quality Report';
  if ((m = x.match(/^(.+) · kalite ekibi performansı$/))) return m[1] + ' · quality team performance';
  if ((m = x.match(/^(\S+)\/10 ile en düşük kriter · ağırlığı %(\d+)$/))) return m[1] + '/10 is the lowest criterion · weight ' + m[2] + '%';
  if ((m = x.match(/^(\d+) işaret · her biri çağrıyı 0 yaptı$/))) return m[1] + ' flags · each one scored that call 0';
  if ((m = x.match(/^(\d+) açık · (\d+) itiraz listeleniyor$/))) return m[1] + ' open · ' + m[2] + ' objections listed';
  if ((m = x.match(/^Kriter ortalaması (\S+) olsa da kritik hata nedeniyle çağrı 0,0 aldı\.$/))) return 'The criteria average was ' + m[1] + ', but a critical error scored the call 0.0.';
  if ((m = x.match(/^Merhaba (.+) 👋$/))) return 'Hello ' + m[1] + ' 👋';
  // agent home (MyDashboard): the brand name, the week count and the average are data, so
  // these are templates rather than fixed strings.
  if ((m = x.match(/^(.+) · çağrı kayıtların, puanların ve itiraz hakkın$/))) return m[1] + ' · your call recordings, your scores and your objection rights';
  if ((m = x.match(/^Bu hafta (\d+) değerlendirme$/))) return m[1] + ' evaluations this week';
  if ((m = x.match(/^ortalama (\S+)\/10 · (.+) ekibinde (\d+)\/(\d+)$/))) return 'average ' + m[1] + '/10 · ' + m[3] + '/' + m[4] + ' in the ' + m[2] + ' team';
  if ((m = x.match(/^ortalama (\S+)\/10$/))) return 'average ' + m[1] + '/10';
  if ((m = x.match(/^(\S+)\/10 · ağırlığı %(\d+) · bu kriterdeki bir puan genel ortalamayı en çok etkileyen alanlardan\.$/))) return m[1] + '/10 · weight ' + m[2] + '% · a point here moves the overall average the most.';
  // The upload hint is built from /api/meta (extension list + AUDIO_MAX_MB), so it is a
  // template now rather than a fixed string.
  if ((m = x.match(/^(.+) · en fazla (\d+) MB$/))) return m[1] + ' · max ' + m[2] + ' MB';
  // …and since 2026-09-10 the size is stated PER FILE, because a call may carry up to
  // meta.audio.maxFiles recordings. Both numbers come from the server, so both are templates.
  if ((m = x.match(/^(.+) · dosya başına en fazla (\d+) MB$/))) return m[1] + ' · max ' + m[2] + ' MB per file';
  if ((m = x.match(/^Çağrı kaydı her çağrıda zorunlu · en fazla (\d+) kayıt eklenebilir\.$/))) {
    return `A call recording is required on every call · up to ${m[1]} recordings.`;
  }
  if ((m = x.match(/^En fazla (\d+) çağrı kaydı eklenebilir\.$/))) {
    return `At most ${m[1]} call recordings can be attached.`;
  }
  if ((m = x.match(/^En fazla (\d+) kayıt · yeni dosya eklenir, mevcut kayıt silinmez\.$/))) {
    return `Up to ${m[1]} recordings · a new file is added, the existing ones are kept.`;
  }
  // the server's own message for the cap (engine/audio-set.js carries the number)
  if ((m = x.match(/^bir değerlendirmeye en fazla (\d+) çağrı kaydı eklenebilir$/))) {
    return `at most ${m[1]} call recordings can be attached to one evaluation`;
  }
  if ((m = x.match(/^kayıt bulunamadı: (\d+)$/))) return 'recording not found: ' + m[1];
  if ((m = x.match(/^geçersiz kayıt id: '(.*)'$/))) return "Invalid recording id: '" + m[1] + "'";
  // server-side validation messages (src/api/users.js) — the API's `message` wins over the code map
  if ((m = x.match(/^geçersiz rol: '(.+)'$/))) return "Invalid role: '" + m[1] + "'";
  if ((m = x.match(/^dönem yılı (\d+)-(\d+) aralığında olmalı: '(.+)'$/))) return 'The period year must be between ' + m[1] + ' and ' + m[2] + ": '" + m[3] + "'";
  if ((m = x.match(/^geçersiz çağrı tarihi: '(.+)'$/))) return "Invalid call date: '" + m[1] + "'";
  if ((m = x.match(/^'(.+)' için marka zorunlu$/))) return "A brand is required for '" + (EN[m[1]] || m[1]) + "'";
  if ((m = x.match(/^bilinmeyen marka: '(.+)'$/))) return "Unknown brand: '" + m[1] + "'";
  if ((m = x.match(/^şifre en az (\d+) karakter olmalı$/))) return 'Password must be at least ' + m[1] + ' characters';
  if ((m = x.match(/^(.+) için yeni şifre:$/))) return 'New password for ' + m[1] + ':';
  return null;
}

/** Translate text nodes, placeholders and titles under root when lang === 'en'. */
function translateDOM(lang: Lang, root: HTMLElement | null): void {
  if (lang !== 'en') return;
  const el = root || document.body;
  if (!el) return;

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode: (t) => {
      const parent = t.parentNode as HTMLElement | null;
      const p = parent && parent.nodeName;
      if (p === 'SCRIPT' || p === 'STYLE') return NodeFilter.FILTER_REJECT;
      // Material Symbols ligatures (`.ms`) and anything marked translate="no" (initials,
      // the TR/EN toggle, weight chips) are never dictionary lookups.
      if (parent && parent.nodeType === 1
        && (parent.classList.contains('ms') || parent.getAttribute('translate') === 'no')) {
        return NodeFilter.FILTER_REJECT;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes: Node[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) nodes.push(node);
  nodes.forEach((n) => {
    const raw = n.nodeValue;
    if (!raw) return;
    const k = raw.trim();
    if (!k) return;
    const e = translatePhrase(k);
    if (e != null && e !== k) n.nodeValue = raw.replace(k, e);
  });

  el.querySelectorAll('input[placeholder], textarea[placeholder]').forEach((input) => {
    const i = input as HTMLInputElement;
    const k = (i.placeholder || '').trim();
    const e = translatePhrase(k);
    if (e != null && e !== k) i.placeholder = e;
  });
  el.querySelectorAll('[title]').forEach((t) => {
    const k = (t.getAttribute('title') || '').trim();
    const e = translatePhrase(k);
    if (e != null && e !== k) t.setAttribute('title', e);
  });
}

/**
 * Apply the EN overlay. Two triggers:
 *   1. useLayoutEffect (no dep array): after every App commit, before paint.
 *   2. MutationObserver: async content that arrives without an App re-render
 *      (fetch results, poll updates, modals).
 * translateDOM is idempotent (already-English nodes match nothing), so the observer
 * cannot loop on its own writes.
 */
export function useTranslateDOM(lang: Lang, rootRef: React.RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    // The document language drives locale-aware `text-transform: uppercase`. Left at 'tr'
    // the EN overlay rendered every uppercase label with a Turkish dotted capital —
    // "TOPİC", "LİST", "WEİGHTED SCORE" — because Turkish maps i → İ.
    document.documentElement.lang = lang;
    translateDOM(lang, rootRef.current);
  });

  useEffect(() => {
    if (lang !== 'en') return;
    const root = rootRef.current;
    if (!root || typeof MutationObserver === 'undefined') return;
    const obs = new MutationObserver(() => translateDOM('en', root));
    obs.observe(root, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, [lang, rootRef]);
}
